import { UseFormReturnType } from '@mantine/form';
import { IFileState } from 'components/files/upload/types';
import UploadFiles from 'components/files/upload/UploadFiles';
import { DeviceFaultFormData } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultForm';
import { useEffect, useMemo } from 'react';
import { IFormInputSpecFileList } from 'pages/revisions-module/template-editor/editors/form/types';

interface IStoredFile {
  fileId: string;
  fileName: string;
  fileType: string;
}

interface DeviceFaultAttachmentsProps {
  index: number;
  spec: IFormInputSpecFileList;
  form: UseFormReturnType<DeviceFaultFormData, (values: DeviceFaultFormData) => DeviceFaultFormData>;
}

/**
 * Attachments input for the device fault form.
 */
export default function DeviceFaultAttachments({ index, form, spec }: DeviceFaultAttachmentsProps) {
  const { value, onChange, error } = form.getInputProps(`faults.${index}.attachments`);

  // Parse accept string to array format expected by UploadFiles
  const acceptArray = useMemo(() => {
    if (!spec.accept || spec.accept === '*/*') {
      return ['*'];
    }
    return spec.accept.split(',').map((type) => type.trim());
  }, [spec.accept]);

  // Initialize the form field if it doesn't exist
  useEffect(() => {
    if (value === undefined) {
      onChange([]);
    }
  }, [value]);

  /**
   * Handles file state changes and updates the form with finalized file data.
   */
  const handleChange = (files: IFileState[] | undefined) => {
    const storedFiles: IStoredFile[] = (files || [])
      .filter((file) => file.finalized && file.fileId)
      .map((file) => ({
        fileId: file.fileId!,
        fileName: file.fileName,
        fileType: file.fileType,
      }));

    onChange(storedFiles);
  };

  // Convert stored file data back to IFileState format for the UploadFiles component
  const initialFiles = useMemo(() => {
    const storedFiles = value as any as IStoredFile[] | undefined;

    if (!storedFiles || !Array.isArray(storedFiles)) {
      return [];
    }

    return storedFiles.map((file) => ({
      uuid: file.fileId,
      progress: 1,
      finalized: true,
      fileId: file.fileId,
      fileName: file.fileName,
      fileType: file.fileType,
    }));
  }, [value]);

  return (
    <UploadFiles
      accept={acceptArray}
      maxFiles={spec.maxFiles}
      onChange={handleChange}
      error={error}
      initialFiles={initialFiles}
      w="100%"
    />
  );
}
